import { Box, Button, Checkbox, Image, Text } from '@timelyapp/react';
import classNames from 'classnames';
import { Brain, InfoIcon } from 'components/Icons';
import { Scrollbars } from 'components/scrollbars';
import Tooltip from 'components/Tooltip';
import { SHOW_MEMORY_DRAWER_CONTENT } from 'constants/user';
import React, { useCallback, useState } from 'react';
import FontAwesome from 'react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import {
  setHourDraftTimestamp,
  toggleTicMemoryIdOnHourDraftTimestamp,
} from 'redux/modules/hours.actions';
import { saveUserProperty } from 'redux/modules/user.actions';
import { getTicMemoryDrawerProps } from 'redux/selectors/hour.draft.tic.memories';
import { TimestampPickerSection } from 'routes/supercalendar/components/calendar/hour-editor';
import type { HourTimestamp } from 'types/hour';
import type { ThunkDispatch } from 'types/redux';
import type { UserTimeFormat } from 'types/user';
import { formatTicMemoryDrawerDuration } from 'utils/memories';
import styles from './MemoryDrawer.module.scss';

interface Props {
  date: string;
  timeFormat: UserTimeFormat;
  snapToMinutes: number;
  onTimestampDelete: (deletedIndex: number) => void;
  disableEditing?: boolean;
}

const MemoryDrawer: React.FC<Props> = ({
  date,
  timeFormat,
  snapToMinutes,
  onTimestampDelete,
  disableEditing,
}) => {
  const [expandedTimestampIndex, setExpandedTimestampIndex] = useState<number>();
  const dispatch = useDispatch<ThunkDispatch>();
  const { timestamps, summary, ui } = useSelector(getTicMemoryDrawerProps);

  const isExpanded = ui.isExpanded;

  const toggleExpandedDrawer = useCallback(() => {
    dispatch(saveUserProperty(SHOW_MEMORY_DRAWER_CONTENT, !isExpanded));
  }, [isExpanded, dispatch]);

  const handleTimestampChange = useCallback(
    (index: number, modifiedTimestamp: HourTimestamp) => {
      dispatch(setHourDraftTimestamp(index, modifiedTimestamp));
    },
    [dispatch]
  );

  const handleMemoryToggle = useCallback(
    (timestampIndex: number, memoryId: number) => {
      dispatch(toggleTicMemoryIdOnHourDraftTimestamp(timestampIndex, memoryId));
    },
    [dispatch]
  );

  const toggleExpandedTimestamp = useCallback(
    (index: number) => {
      const isNotEmpty = Object.keys(timestamps[index].memory_associations).length > 0;
      if (isNotEmpty) {
        const newValue = expandedTimestampIndex === index ? undefined : index;
        setExpandedTimestampIndex(newValue);
      }
    },
    [expandedTimestampIndex, timestamps]
  );

  const handleTimestampDelete = useCallback(
    (event: React.MouseEvent, index: number) => {
      event.stopPropagation();
      onTimestampDelete(index);
      setExpandedTimestampIndex(undefined);
    },
    [onTimestampDelete]
  );

  const isGreyedOut = !ui.isAutoUpdated || timestamps.length === 0;

  return (
    <div className={styles.componentContainer}>
      <Box.Row
        padding="large"
        gap="medium"
        className={styles.sectionContainer}
        onClick={toggleExpandedDrawer}
      >
        <Box.Row alignItems="center" className={styles.headerTitle} gap="medium">
          <Brain width={14} />
          <Text.Label size="small" color={isGreyedOut ? 'lowContrast' : undefined}>
            Memories
          </Text.Label>
        </Box.Row>

        <Box.Spacer />

        {!ui.isAutoUpdated && (
          <Text.Label color="lowContrast" size="xsmall">
            <FontAwesome name="clock" /> {formatTicMemoryDrawerDuration(summary.total_seconds)}
          </Text.Label>
        )}

        <Text.Label color="lowContrast" size="xsmall">
          {summary.num_memories_checked} / {summary.num_memories}
        </Text.Label>

        <Text.Label color="lowContrast">
          <FontAwesome name={isExpanded ? 'caret-up' : 'caret-down'} />
        </Text.Label>
      </Box.Row>

      {/*
        Individual memories below..
      */}
      {isExpanded && (
        <Scrollbars autoHeight autoHeightMax="40vh">
          <Box.Column>
            {timestamps.length === 0 && <Placeholder />}

            {timestamps.map((timestamp, index) => (
              <Box.Column key={timestamp.id || index}>
                <Box.Row
                  padding="large"
                  gap="large"
                  alignItems="center"
                  className={classNames(styles.timestampRowHeader, {
                    [styles.disabled]: disableEditing,
                  })}
                  onClick={() => toggleExpandedTimestamp(index)}
                >
                  <Text.Label color="minContrast">
                    <FontAwesome
                      name={expandedTimestampIndex === index ? 'caret-up' : 'caret-down'}
                    />
                  </Text.Label>
                  <Box.Row gap="medium">
                    <TimestampPickerSection
                      key={index}
                      timestamp={timestamp}
                      date={date}
                      timeFormat={timeFormat}
                      snapToMinutes={snapToMinutes}
                      onChange={timestamp => handleTimestampChange(index, timestamp)}
                      compactStyle={true}
                      standalone={true}
                      disabled={disableEditing}
                      isTimerRunning={false}
                      showInput
                    />
                    <Text.Label size="xsmall" color="lowContrast" ellipsis>
                      {formatTicMemoryDrawerDuration(timestamp.total_seconds)}
                    </Text.Label>
                    <Box.Spacer />

                    <Box.Row gap="small" className={styles.timestampSummaryIcons}>
                      {timestamp.meta.icons.map(
                        (summary, index) =>
                          summary.icon_url && (
                            <Image
                              key={index}
                              src={summary.icon_url}
                              style={{ width: 16, borderRadius: 4 }}
                              className={classNames('fs-exclude')}
                            />
                          )
                      )}

                      {timestamp.meta.iconsCount && (
                        <Text.Label size="xsmall" color="lowContrast">
                          +{timestamp.meta.iconsCount}
                        </Text.Label>
                      )}
                    </Box.Row>

                    {!!timestamp.meta.title && (
                      <Text.Label size="xsmall" color="lowContrast">
                        {timestamp.meta.title}
                      </Text.Label>
                    )}
                    <Button
                      size="xsmall"
                      priority="xlow"
                      onClick={event => handleTimestampDelete(event, index)}
                      disabled={disableEditing}
                    >
                      <FontAwesome name="times" />
                    </Button>
                  </Box.Row>
                </Box.Row>
                {expandedTimestampIndex === index && (
                  <Box.Column padding="large">
                    {timestamp.memory_associations.map(memory => {
                      return (
                        memory && (
                          <Box.Row
                            key={memory.id}
                            gap="medium"
                            padding="small"
                            onClick={() => handleMemoryToggle(index, memory.id)}
                            className={styles.memoryItem}
                          >
                            <Checkbox checked={memory.is_checked} />

                            {memory.icon_url && (
                              <Image
                                src={memory.icon_url}
                                style={{ width: 16 }}
                                className={classNames('fs-exclude')}
                              />
                            )}

                            <Text.Label
                              size="small"
                              ellipsis
                              hideFromFullstory
                              color={!memory.is_checked ? 'subtle' : undefined}
                              // style={{ minWidth: 0 }}
                            >
                              {memory.title}
                            </Text.Label>
                            <Box.Spacer />

                            <Text.Label size="xsmall" color="lowContrast">
                              {formatTicMemoryDrawerDuration(memory.total_seconds)}
                            </Text.Label>

                            <Tooltip
                              position="left"
                              title={
                                memory.is_checked
                                  ? 'Explicitly logged'
                                  : 'Indirectly logged via timestamp'
                              }
                            >
                              <InfoIcon />
                            </Tooltip>
                          </Box.Row>
                        )
                      );
                    })}
                  </Box.Column>
                )}
              </Box.Column>
            ))}
          </Box.Column>
        </Scrollbars>
      )}
    </div>
  );
};

const Placeholder: React.FC = () => (
  <div className={styles.placeholder}>
    <div className={styles.placeholderTitle}>Feed me time and memories!</div>
    <div className={styles.placeholderBody}>
      Logged time can <strong>auto-calculate</strong> when you choose Memories, click & drag the
      Timeline, or run timers.
    </div>
  </div>
);

export default React.memo(MemoryDrawer);
